import { 
  ExerciseItem, 
  VerifyExerciseRequest, 
  VerifyExerciseResponse
} from "./exercise.interfaces";

/**
 * Mock de exercícios para testes e desenvolvimento
 */
const mockExercises: ExerciseItem[] = [
  {
    id: "1",
    type: "watch-large-video",
    name: "Introdução ao Presente Contínuo",
    description: "Assista ao vídeo e responda às questões seguintes",
    videoUrl: "https://example.com/video/present-continuous"
  },
  {
    id: "2",
    type: "multiple-choices",
    name: "Selecione a opção correta",
    description: "______ is your favorite color?",
    options: [
      { id: "What", text: "What", isCorrect: true },
      { id: "Who", text: "Who", isCorrect: false },
      { id: "Where", text: "Where", isCorrect: false },
      { id: "Why", text: "Why", isCorrect: false },
      { id: "When", text: "When", isCorrect: false }
    ],
    correctOption: "What"
  },
  {
    id: "3",
    type: "multiple-choices",
    name: "Selecione a opção correta",
    description: "______ are you going to the party?",
    options: [
      { id: "What", text: "What", isCorrect: false },
      { id: "Who", text: "Who", isCorrect: false },
      { id: "Where", text: "Where", isCorrect: false },
      { id: "Why", text: "Why", isCorrect: false },
      { id: "When", text: "When", isCorrect: true }
    ],
    correctOption: "When"
  },
  {
    id: "4",
    type: "fill-in-the-text",
    name: "Complete a frase",
    description: "Complete a frase com as palavras corretas",
    text: "The quick brown {fox|cat|wolf} jumps over the lazy {dog|bike|chair}.",
    correctAnswers: {
      "0": "fox",
      "1": "dog"
    }
  },
  {
    id: "5",
    type: "fill-in-the-text",
    name: "Complete as frases",
    description: "Complete as frases com os verbos no presente contínuo",
    text: "I {am studying|am study|studying} English right now. My friend {is watching|is watch|watching} TV.",
    correctAnswers: {
      "0": "am studying",
      "1": "is watching"
    }
  },
  {
    id: "6",
    type: "comprehension-question",
    name: "Compreensão de texto",
    description: "A menina estava fazendo o quê e com quem?",
    expectedKeywords: ["walking", "mother"],
    textPassage: "The girl was walking in the park with her mother on a sunny day."
  },
  {
    id: "7",
    type: "comprehension-question",
    name: "Explique o conceito",
    description: "Explique quando usamos o presente contínuo em inglês",
    expectedKeywords: ["ações", "momento", "temporárias", "progresso"]
  }
];

/**
 * Busca exercícios para um módulo específico (simulado)
 * @returns Lista simulada de exercícios
 */
export const fetchExercisesByModule = async (): Promise<ExerciseItem[]> => {
  // Simulação de delay para simular uma chamada de API
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return mockExercises;
};

/**
 * Avalia a resposta de um exercício (simulado)
 * @param data Dados do exercício e resposta do aluno
 * @returns Objeto com o resultado simulado da avaliação
 */
export const mockEvaluateExerciseAnswer = async (data: VerifyExerciseRequest): Promise<VerifyExerciseResponse> => {
  // Simulação de delay para simular uma chamada de API
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  return simulateEvaluation(data);
};

/**
 * Função auxiliar para simular a avaliação de exercícios
 * @param data Dados do exercício e resposta do aluno
 * @returns Objeto com o resultado simulado da avaliação
 */
function simulateEvaluation(data: VerifyExerciseRequest): VerifyExerciseResponse {
  const { exerciseType, answer, exerciseId } = data;
  
  // Buscar o exercício correspondente
  const exercise = mockExercises.find(e => e.id === exerciseId);
  
  if (!exercise) {
    return {
      isCorrect: false,
      feedback: "Exercício não encontrado."
    };
  }
  
  switch (exerciseType) {
    case "multiple-choices":
    case "multiple_choice": {
      // Ensure answer is an array for multiple_choice, as per component logic
      const submittedAnswers = Array.isArray(answer) ? answer : [answer];

      // Determine correct options
      // Adapting to current mock structure, ideally options have isCorrect
      let correctOptionsForExercise: string[] = [];
      if (exercise.options && exercise.options.every(opt => typeof opt.isCorrect === 'boolean')) {
        correctOptionsForExercise = exercise.options
          .filter(opt => opt.isCorrect)
          .map(opt => opt.text);
      } else if (exercise.correctOption && exercise.options) {
        // Fallback for older mock structure
        const correctOpt = exercise.options.find(opt => opt.text === exercise.correctOption || opt.id === exercise.correctOption);
        if (correctOpt) {
          correctOptionsForExercise = [correctOpt.text];
        }
      }


      // Evaluate correctness
      // For multiple choice, all selected options must be correct, and all correct options must be selected.
      const isCorrect = submittedAnswers.length === correctOptionsForExercise.length &&
                        submittedAnswers.every(ans => correctOptionsForExercise.includes(ans)) &&
                        correctOptionsForExercise.every(correctOpt => submittedAnswers.includes(correctOpt));

      return {
        isCorrect,
        feedback: isCorrect
          ? "Parabéns! Você escolheu a(s) resposta(s) correta(s)."
          : "Tente novamente. A(s) resposta(s) correta(s) é(são): " + correctOptionsForExercise.join(", "),
        expectedAnswer: isCorrect ? undefined : correctOptionsForExercise,
        userAnswer: submittedAnswers
      };
    }
      
    case "fill-in-the-text":
    case "fill_in_the_text": {
      if (exerciseId === "4" || exerciseId === "5") {
        const correctAnswers = exercise.correctAnswers || {};
        const allCorrect = Object.entries(correctAnswers).every(
          ([index, value]) => answer[index] === value
        );
        
        return {
          isCorrect: allCorrect,
          feedback: allCorrect 
            ? "Excelente! Você completou corretamente todas as frases."
            : "Revise suas respostas. A resposta correta seria: " + 
              Object.entries(correctAnswers).map(([index, value]) => 
                `espaço ${parseInt(index)+1}: "${value}"`
              ).join(", "),
          correctAnswer: correctAnswers
        };
      } else {
        // Para compatibilidade com o componente existente
        const correctAnswers = { "0": "fox", "1": "dog" };
        const allCorrect = Object.entries(correctAnswers).every(
          ([index, value]) => answer[index] === value
        );
        
        return {
          isCorrect: allCorrect,
          feedback: allCorrect 
            ? "Muito bem! Você completou corretamente a frase."
            : "Revise suas respostas. A frase correta é: 'The quick brown fox jumps over the lazy dog.'",
          correctAnswer: correctAnswers
        };
      }
    }
      
    case "comprehension-question":
    case "comprehension_question": {
      if (exerciseId === "6" || exerciseId === "7") {
        const expectedKeywords = exercise.expectedKeywords || [];
        const hasKeywords = typeof answer === 'string' && 
                            expectedKeywords.some(keyword => 
                              answer.toLowerCase().includes(keyword.toLowerCase())
                            );
        
        return {
          isCorrect: hasKeywords,
          feedback: hasKeywords
            ? "Boa resposta! Você demonstrou compreensão do conceito."
            : "Sua resposta poderia ser mais completa. Tente incluir os conceitos principais do presente contínuo."
        };
      } else {
        // Para compatibilidade com o componente existente
        const hasKeywords = typeof answer === 'string' && 
                            answer.toLowerCase().includes("walking") && 
                            answer.toLowerCase().includes("mother");
        
        return {
          isCorrect: hasKeywords,
          feedback: "Verifique se você mencionou que a garota estava caminhando no parque com sua mãe. A resposta deve incluir ambos os elementos para estar completa."
        };
      }
    }
    
    case "watch-large-video":
    case "watch-simple-video":
    case "watch_large_video":
    case "watch_simple_video": {
      return {
        isCorrect: true,
        feedback: "Vídeo assistido com sucesso. Agora responda às questões seguintes."
      };
    }
      
    default:
      return {
        isCorrect: false,
        feedback: "Tipo de exercício não suportado."
      };
  }
} 